/**
 * Owner: Masa Bayu
 * File: entitlement-boundary.ts
 * Date:Time: 2026-09-15 19:18
 *   Description: QXRead T0 entitlement boundary — verify only · PE1 contentId edition key.
 */
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
import { assertNoForbiddenFoundationKey } from './authority';
export const QXREAD_ENTITLEMENT_STATES = [
    'pending_grant',
    'active',
    'grace',
    'expired',
    'revoked',
    'grant_failed',
];
export const QXREAD_ENTITLEMENT_SCOPES = [
    'preview',
    'owned',
    'subscription',
    'licensed',
    'author',
    'admin',
];
function assertEnum(value, allowed, label) {
    if (typeof value !== 'string' || !allowed.includes(value)) {
        throw new Error(`${label} must be one of: ${allowed.join(', ')}`);
    }
    return value;
}
export function createQxreadEntitlement(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead entitlement');
    assertValidId(options.accountId, 'QXRead entitlement accountId');
    const contentId = options.contentId ?? options.publicationId;
    const publicationId = options.publicationId ?? options.contentId;
    if (contentId === undefined || publicationId === undefined) {
        throw new Error('QXRead entitlement requires contentId or publicationId');
    }
    assertValidId(contentId, 'QXRead entitlement contentId');
    assertValidId(publicationId, 'QXRead entitlement publicationId');
    if (contentId !== publicationId) {
        throw new Error('PE1 flat model requires contentId === publicationId (edition key); New Edition uses a new contentId');
    }
    const entitlementId = options.entitlementId !== undefined ? options.entitlementId : createQxreadId('qxrdent');
    assertValidId(entitlementId, 'QXRead entitlementId');
    const createdAt = options.createdAt ?? new Date().toISOString();
    assertTimestamp(createdAt, 'QXRead entitlement createdAt');
    let expiresAt = null;
    if (options.expiresAt !== undefined && options.expiresAt !== null) {
        assertTimestamp(options.expiresAt, 'QXRead entitlement expiresAt');
        expiresAt = options.expiresAt;
    }
    return Object.freeze({
        entitlementId,
        accountId: options.accountId,
        publicationId,
        contentId,
        scope: assertEnum(options.scope, QXREAD_ENTITLEMENT_SCOPES, 'QXRead entitlement scope'),
        state: assertEnum(options.state ?? 'pending_grant', QXREAD_ENTITLEMENT_STATES, 'QXRead entitlement state'),
        createdAt,
        expiresAt,
    });
}
/** PE1 helper — edition key from entitlement SoT. */
export function entitlementContentId(entitlement) {
    return entitlement.contentId;
}
export function isEntitlementActiveForRead(entitlement) {
    return entitlement.state === 'active' || entitlement.state === 'grace';
}
/**
 * Foundation authorization helper for Reader launch.
 * Does not execute QXReader and does not mint grants.
 */
export function authorizeReaderLaunch(input) {
    if (!input.sessionAuthenticated || input.accountId === null) {
        return Object.freeze({
            allowed: false,
            reason: 'Authenticated session required for protected reader launch',
        });
    }
    if (!input.publicationBound) {
        return Object.freeze({
            allowed: false,
            reason: 'Publication must be bound to a QXP reference before launch',
        });
    }
    if (input.entitlement === null) {
        return Object.freeze({
            allowed: false,
            reason: 'Entitlement required — catalog reference is not content access',
        });
    }
    if (input.entitlement.accountId !== input.accountId) {
        return Object.freeze({
            allowed: false,
            reason: 'Entitlement accountId must match session accountId',
        });
    }
    if (!isEntitlementActiveForRead(input.entitlement)) {
        return Object.freeze({
            allowed: false,
            reason: `Entitlement state "${input.entitlement.state}" does not permit launch`,
        });
    }
    if (input.requireFullAccess !== false && input.entitlement.scope === 'preview') {
        return Object.freeze({
            allowed: false,
            reason: 'Preview entitlement cannot authorize full reader launch',
        });
    }
    return Object.freeze({ allowed: true, reason: 'Entitlement verified for reader launch' });
}
//# sourceMappingURL=entitlement-boundary.js.map